import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useStore, type StoreApi } from 'zustand';
import { CircleAlert, Info, Loader2, RefreshCw, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { PanelSkeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/workspace-dialog';
import { cn } from '@/lib/utils';
import { usePicklistStore } from '../../store/picklistStore';
import { PicklistAdjustmentReason, type PicklistItemGroupDto } from '../../types';
import { buildAdjustModel, findGroup, type AdjustForm } from '../../utils/adjustModel';
import { buildAdjustRequest } from '../../utils/adjustRequest';
import { classifyAdjustFailure } from '../../utils/adjustErrors';
import { AdjustBinCard } from './AdjustBinCard';
import { MoveDialog } from './MoveDialog';
import { createAdjustDialogStore, type AdjustDialogState } from './adjustDialogStore';

interface AdjustDialogProps {
  /** An item id: the group's anchor or any line id of it (PT-6 scope 14). Null: closed. */
  targetId: number | null;
  onClose: () => void;
}

const REASONS: [PicklistAdjustmentReason, string][] = [
  [PicklistAdjustmentReason.WrongItem, 'Wrong item'],
  [PicklistAdjustmentReason.WrongQuantity, 'Wrong quantity'],
  [PicklistAdjustmentReason.WrongBin, 'Wrong bin'],
  [PicklistAdjustmentReason.Other, 'Other'],
];

/**
 * PT-6 — web Adjust per bin × MRS line (replaces `AdjustItemDialog`). A workspace dialog, 620px, top-aligned, with a
 * fixed header and footer and a scrolling body (prototype L1298–1503).
 *
 * It renders from a SNAPSHOT of the item group, taken when it opens and again after a 409 reload, never from the live
 * store: a detail refetch while it is open (the settle-poll, another tab) changes neither the figures nor what was
 * typed. Staleness is the 409's job, against the `adjustVersion` the snapshot carries.
 */
export function AdjustDialog({ targetId, onClose }: AdjustDialogProps) {
  const open = targetId != null;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent
        hideCloseButton
        overlayClassName="bg-black/50"
        className="top-[5vh] flex max-h-[90vh] w-[720px] max-w-[calc(100%-48px)] translate-y-0 flex-col gap-0 overflow-hidden rounded-[14px] bg-card p-0 sm:rounded-[14px]"
      >
        {open && <AdjustBody targetId={targetId} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function AdjustBody({ targetId, onClose }: { targetId: number; onClose: () => void }) {
  const picklistId = usePicklistStore((s) => s.selectedPicklist?.id ?? 0);
  const [store] = useState(createAdjustDialogStore);
  const [snapshot, setSnapshot] = useState<PicklistItemGroupDto | null>(
    () => findGroup(usePicklistStore.getState().selectedPicklist?.itemGroups, targetId),
  );
  /** Take the group from the store again. */
  const resnapshot = useCallback(() => {
    setSnapshot(findGroup(usePicklistStore.getState().selectedPicklist?.itemGroups, targetId));
  }, [targetId]);
  const [reloading, setReloading] = useState(false);
  const [reloadError, setReloadError] = useState<string | null>(null);

  /** Reload the picklist quietly (the page keeps its content) and take a new snapshot of the group. */
  const reload = useCallback(async () => {
    setReloading(true);
    setReloadError(null);
    const error = await usePicklistStore.getState().reloadSelectedPicklist(picklistId);
    if (error) {
      setReloadError(error);
    } else {
      resnapshot();
    }
    setReloading(false);
  }, [picklistId, resnapshot]);

  // Opened with no version in hand (Add or Remove Picker answer the raw read): reload first, without the amber note,
  // and never send an empty version (C1).
  const needsVersion = snapshot != null && !snapshot.adjustVersion;
  useEffect(() => {
    if (needsVersion) void reload();
  }, [needsVersion, reload]);

  const reason = useStore(store, (s) => s.reason);
  const notesEmpty = useStore(store, (s) => s.notes.trim() === '');
  const vals = useStore(store, (s) => s.vals);
  const ser = useStore(store, (s) => s.ser);
  const disp = useStore(store, (s) => s.disp);
  const stale = useStore(store, (s) => s.stale);
  const applyError = useStore(store, (s) => s.applyError);
  const submitting = useStore(store, (s) => s.submitting);
  const planKey = useStore(store, (s) => s.planKey);
  const moveSource = useStore(store, (s) => s.moveSource);

  // The note's TEXT stays out of the model: only whether it is empty reaches it (Other needs one), so a note keystroke
  // never rebuilds the cards.
  const form: AdjustForm = { reason, notes: notesEmpty ? '' : 'x', vals, ser, disp };
  const model = snapshot ? buildAdjustModel(snapshot, form) : null;
  const canApply = !!model?.canApply && !!snapshot?.adjustVersion && !reloading && !reloadError && !submitting;

  const submit = async () => {
    if (!snapshot) return;
    const s = store.getState();
    const request = buildAdjustRequest(buildAdjustModel(snapshot, s), s, snapshot.adjustVersion);
    if (!request) return;
    s.setSubmitting(true);
    s.setApplyError(null);
    try {
      await usePicklistStore.getState().adjustItem(snapshot.id, request);
      s.setSubmitting(false);
      onClose();
    } catch (error) {
      s.setSubmitting(false);
      const failure = classifyAdjustFailure(error);
      if (failure.kind === 'stale') {
        // B4 — the amber note at once, the numbers dropped, then the reload re-seeds the form from new figures.
        s.markStale();
        await reload();
      } else {
        s.setApplyError(failure.text);
      }
    }
  };

  // PT-6b — a Move saved from its own dialog. The store already holds the answer's figures, so take a new snapshot
  // from it, drop every number (they were against the old figures) and say what happened.
  const moved = (text: string) => {
    resnapshot();
    store.getState().resetAfterMove();
    toast.success(text);
  };

  const code = snapshot?.itemCode ?? '';
  const name = snapshot?.itemName ?? '';

  return (
    <>
      <div className="flex shrink-0 items-start justify-between gap-3 border-b border-border px-5 pb-3.5 pt-[18px]">
        <div className="min-w-0">
          <DialogTitle className="text-base font-bold">
            Adjust <span className="font-mono">{code}</span>
          </DialogTitle>
          <DialogDescription className="mt-1 text-[12.5px]">
            {name} — correct the recorded pick: lower it, or move units to another MRS line.
          </DialogDescription>
        </div>
        <IconButton label="Close" onClick={onClose} className="h-[30px] w-[30px]">
          <X className="h-[15px] w-[15px]" aria-hidden />
        </IconButton>
      </div>

      <div
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-5 pb-5 pt-4"
        // Scrolling the body closes an open breakdown card (the prototype's adj.onBodyScroll).
        onScroll={() => { if (planKey) store.getState().setPlanKey(null); }}
      >
        {stale && (
          <div role="alert" className="flex items-start gap-2.5 rounded-[10px] border border-warning/35 bg-warning/[0.08] px-3 py-2.5">
            <RefreshCw className="mt-0.5 h-[15px] w-[15px] shrink-0 text-warning" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-[12.5px] font-semibold text-foreground">Nothing was applied — the figures changed</div>
              <div className="mt-0.5 text-xs leading-[1.45] text-muted-foreground">
                Someone changed this item while you had it open. The dialog now shows the new figures — make your change again.
              </div>
            </div>
            <IconButton label="Dismiss" onClick={() => store.getState().dismissStale()} className="h-[26px] w-[26px] rounded-md">
              <X className="h-3.5 w-3.5" aria-hidden />
            </IconButton>
          </div>
        )}

        <div className="flex gap-2 rounded-lg border border-info/30 bg-info/[0.08] px-[11px] py-[9px]">
          <Info className="mt-px h-3.5 w-3.5 shrink-0 text-info" aria-hidden />
          <p className="text-xs leading-normal text-info">
            This corrects the <b>recorded pick</b> before it reaches NetSuite — nothing has moved in inventory yet.{' '}
            {model?.bannerTail}
          </p>
        </div>

        <ReasonField store={store} reason={reason} />

        <div>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2.5">
            <span className="text-xs font-semibold">
              Bins ({model?.unit ?? ''})
              {model?.bulkSummary && <span className="font-medium text-warning"> · {model.bulkSummary}</span>}
            </span>
            {model?.bulkShow && (
              <div className="flex gap-1.5">
                <button
                  type="button"
                  onClick={() => store.getState().returnAll(model.returnAllKeys)}
                  className="h-[26px] rounded-[7px] border border-warning/45 bg-warning/[0.08] px-2.5 text-[11.5px] font-medium text-warning focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Unpick all unposted
                </button>
                <button
                  type="button"
                  onClick={() => store.getState().keepAll()}
                  className="h-[26px] rounded-[7px] border border-input bg-card px-2.5 text-[11.5px] font-medium text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  Reset
                </button>
              </div>
            )}
            {model?.qtyReset && (
              <button
                type="button"
                onClick={() => store.getState().qtyReset()}
                className="inline-flex h-[26px] items-center gap-[5px] rounded-[7px] border border-input bg-card px-2.5 text-[11.5px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <RotateCcw className="h-3 w-3" aria-hidden />
                Reset
              </button>
            )}
            {model?.showHint && <span className="text-[11px] text-muted-foreground">{model.hintText}</span>}
          </div>

          {reloading ? (
            <PanelSkeleton rows={3} />
          ) : reloadError ? (
            <EmptyState
              size="compact"
              title={reloadError}
              action={
                <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={() => void reload()}>
                  <RotateCcw className="h-3 w-3" aria-hidden />
                  Retry
                </Button>
              }
            />
          ) : !model ? (
            <EmptyState size="compact" title="This item is no longer on the picklist." />
          ) : (
            <div className="flex flex-col gap-2.5">
              {model.cards.map((card) => (
                <AdjustBinCard
                  key={card.key}
                  card={card}
                  store={store}
                  openPlanKey={planKey && card.rows.some((r) => r.key === planKey) ? planKey : null}
                />
              ))}
            </div>
          )}
        </div>

        <NotesField store={store} />
      </div>

      <div className="flex shrink-0 items-center justify-end gap-2 border-t border-border bg-card px-5 py-3">
        {applyError && (
          <div role="alert" className="mr-auto flex min-w-0 items-start gap-1.5 text-xs leading-[1.4] text-destructive">
            <CircleAlert className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
            <span>{applyError}</span>
          </div>
        )}
        <Button variant="outline" className="h-9 text-[12.5px]" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button className="h-9 gap-1.5 text-[12.5px] font-semibold" disabled={!canApply} onClick={() => void submit()}>
          {submitting && <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden />}
          Apply adjustment
        </Button>
      </div>

      <MoveDialog
        source={moveSource}
        group={snapshot}
        onClose={() => store.getState().closeMove()}
        onMoved={moved}
        onStale={reload}
      />
    </>
  );
}

function ReasonField({ store, reason }: { store: StoreApi<AdjustDialogState>; reason: PicklistAdjustmentReason }) {
  return (
    <div>
      <div className="mb-1.5 text-xs font-semibold">
        Reason <span className="text-destructive">*</span>
      </div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Reason">
        {REASONS.map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={reason === value}
            onClick={() => store.getState().setReason(value)}
            className={cn(
              'h-7 rounded-full border px-[11px] text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              reason === value ? 'border-primary bg-primary/[0.08] text-primary' : 'border-border bg-card text-muted-foreground',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {reason === PicklistAdjustmentReason.WrongBin && (
        <div className="mt-2 flex items-start gap-2 rounded-lg border border-info/30 bg-info/[0.08] px-2.5 py-2">
          <Info className="mt-px h-3.5 w-3.5 shrink-0 text-info" aria-hidden />
          <span className="text-xs leading-[1.45] text-foreground">
            The bin of a pick can’t be changed here. Lower it in the wrong bin and choose{' '}
            <b className="font-semibold">Returned to bin</b>, then pick it again from the right bin on the handheld.
          </span>
        </div>
      )}
    </div>
  );
}

/** Notes: its own subscription, so typing re-renders only this field (and the footer when it turns empty). */
function NotesField({ store }: { store: StoreApi<AdjustDialogState> }) {
  const notes = useStore(store, (s) => s.notes);
  const reason = useStore(store, (s) => s.reason);
  const needNote = reason === PicklistAdjustmentReason.Other && !notes.trim();
  return (
    <div>
      <label htmlFor="adjust-notes" className="mb-1.5 block text-xs font-semibold">
        Notes {needNote && <span className="font-medium text-destructive">— required for “Other”</span>}
      </label>
      <div className="relative">
        <textarea
          id="adjust-notes"
          value={notes}
          onChange={(e) => store.getState().setNotes(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Anything that explains the correction"
          className="block w-full resize-y rounded-lg border border-input bg-background px-[11px] pb-4 pt-2 text-[12.5px] text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        />
        {notes && (
          <span className="pointer-events-none absolute bottom-1 right-2.5 text-[10px] tabular-nums text-muted-foreground">
            {notes.length} / 500
          </span>
        )}
      </div>
    </div>
  );
}

function IconButton({ label, onClick, className, children }: { label: string; onClick: () => void; className?: string; children: ReactNode }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          aria-label={label}
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-[7px] text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            className,
          )}
        >
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
